/* eslint-disable react/display-name */
import React, { useEffect } from 'react';
import { Box, HStack } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { Header } from '../../app/topBar/Header';
import { TodoCounts } from './TodoCounts';
import { RefetchTodo } from './RefetchTodo';
import { TodoTable } from './todoTable/TodoTable';
import { DashboardTodoSummary } from './DashboardTodoSummary';
import useLookupsByType from '../../app/hooks/useLookupsByType';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import { CandidateMessagingModal } from './todoTable/CandidateMessagingModal';
import useContactRequestDeclineReasons from '../messaging/seeker/candidate/history/useContactRequestDeclinedReasons';
import { useUpcomingInterviewsCount } from './utils/useUpcomingInterviewsCount';

export const Dashboard = React.memo(function Dashboard() {
  const { t } = useTranslation('toDoList');
  const { fetchTodos, resetState, fetchUpcomingAppointments } = useStoreActions((actions) => actions.todo);
  const accountId = useStoreState((state) => state.app.accounts?.id);
  const counts = useStoreState((state) => state.todo.counts);
  const interviewCounts = useStoreState((state) => state.todo.interviewCounts);

  const { currentUserProfile } = useCurrentUserProfile();
  const newCount = useUpcomingInterviewsCount(currentUserProfile?.id);

  const declinedReasonsTypes = useContactRequestDeclineReasons();
  const lookups = useLookupsByType('candidateTodo');
  const offerRejectedReasons = useLookupsByType('offerRejectedReason');

  const memoizedDeclinedReasonsTypes = React.useMemo(() => declinedReasonsTypes, [declinedReasonsTypes]);
  const memoizedCandidateTodos = React.useMemo(() => lookups[0]?.todo, [lookups]);
  const memoizedOfferRejectedReasons = React.useMemo(() => offerRejectedReasons, [offerRejectedReasons]);

  React.useEffect(() => {
    resetState();
  }, [resetState]);

  useEffect(() => {
    if (accountId) {
      fetchTodos({ accountId, needCompleted: false }).catch((error) => {
        console.error('Failed to fetch todos:', error);
      });
    }
  }, [fetchTodos, accountId]);

  useEffect(() => {
    if (accountId) {
      fetchUpcomingAppointments({ accountId }).catch((error) => {
        console.error('Failed to fetch upcoming interviews:', error);
      });
    }
  }, [fetchUpcomingAppointments, accountId]);

  useEffect(() => {
    if (accountId && newCount) {
      fetchUpcomingAppointments({
        accountId,
        todoCount: interviewCounts?.toDo ?? counts?.toDo,
        interviewCount: interviewCounts?.upcomingInterviews,
        total: interviewCounts?.total ?? counts?.total,
      }).catch((err) => {
        console.error('Failed to get upcoming interviews count-=-=', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUpcomingAppointments, accountId, newCount]);

  return (
    <Box>
      <Header showMenu showPageTitle={t('toDoList:pageTitle')} />
      <HStack>
        <TodoCounts />
        <RefetchTodo />
      </HStack>
      <HStack align="flex-start" spacing={0}>
        <DashboardTodoSummary />

        <TodoTable
          declinedReasonsTypes={memoizedDeclinedReasonsTypes}
          candidateTodos={memoizedCandidateTodos}
          offerRejectedReasons={memoizedOfferRejectedReasons}
        />
      </HStack>
      <CandidateMessagingModal />
    </Box>
  );
});
